import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Customer, Topic } from '@anchor-comms/shared';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreateConversation, useAddInboundMessage } from './queries';

export function NewConversationPage() {
  const navigate = useNavigate();
  const create = useCreateConversation();

  const topicsQuery = useQuery({
    queryKey: ['topics'],
    queryFn: () => api<Topic[]>('/api/v1/topics'),
  });
  const customersQuery = useQuery({
    queryKey: ['customers'],
    queryFn: () => api<{ items: Customer[] }>('/api/v1/customers', { query: { limit: 50 } }),
  });

  const [customerId, setCustomerId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [pastedThread, setPastedThread] = useState('');

  // Stash the new conversation id so the inbound message effect can fire after creation
  const [pendingThreadFor, setPendingThreadFor] = useState<string | null>(null);
  const addInbound = useAddInboundMessage(pendingThreadFor ?? '');

  useEffect(() => {
    if (pendingThreadFor && pastedThread.trim()) {
      addInbound.mutate(
        { role: 'customer', content: pastedThread.trim() },
        {
          onSettled: () => navigate(`/conversations/${pendingThreadFor}`),
        },
      );
    } else if (pendingThreadFor) {
      navigate(`/conversations/${pendingThreadFor}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingThreadFor]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId || !topicId) return;
    create.mutate(
      { customerId, topicId },
      {
        onSuccess: (c) => setPendingThreadFor(c.id),
      },
    );
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>New conversation</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <Label htmlFor="customer">Customer</Label>
              <select
                id="customer"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                required
              >
                <option value="">Select a customer…</option>
                {customersQuery.data?.items.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.company ? `· ${c.company}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Don&apos;t see them? Add the customer in the Customers tab first.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="topic">Topic</Label>
              <select
                id="topic"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={topicId}
                onChange={(e) => setTopicId(e.target.value)}
                required
              >
                <option value="">Select a topic…</option>
                {topicsQuery.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="thread">Existing thread (optional)</Label>
              <Textarea
                id="thread"
                rows={6}
                placeholder="Paste the customer&apos;s last message or the existing thread here"
                value={pastedThread}
                onChange={(e) => setPastedThread(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Added as a customer message so the AI has context for the first draft.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || !customerId || !topicId}>
                {create.isPending ? 'Creating…' : 'Create conversation'}
              </Button>
            </div>

            {create.error && (
              <p className="text-sm text-destructive">{(create.error as Error).message}</p>
            )}

            {(!topicsQuery.data || topicsQuery.data.length === 0) && (
              <Input type="hidden" />
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
